// Port of ios/Sources/ViewModels/MessageEditRights.swift (Kotlin: the edit/delete rule in
// `MessageActions.available`).
//
// Own SYNCED posts: always both (a pending post has no server id to patch, a failed one has its
// retry affordance). Someone ELSE's: only with the permission the server checks for exactly that —
// a system admin's powers on the desktop (user, 2026-10-12). Read from the resolved permission names
// (shared/API.md §16.1, §26.1), never from a role, and only for ordinary messages — a `system_*`
// post is nobody's text.

import { PostState, type PostRecord } from './records';

export const EDIT_OTHERS_POSTS = 'edit_others_posts';
export const DELETE_OTHERS_POSTS = 'delete_others_posts';

export interface EditRights {
  canEdit: boolean;
  canDelete: boolean;
}

export const NO_EDIT_RIGHTS: EditRights = Object.freeze({ canEdit: false, canDelete: false });
const FULL_RIGHTS: EditRights = Object.freeze({ canEdit: true, canDelete: true });
const EDIT_ONLY: EditRights = Object.freeze({ canEdit: true, canDelete: false });
const DELETE_ONLY: EditRights = Object.freeze({ canEdit: false, canDelete: true });

/** One shared object per combination: a row's memo compares props by identity (perf pass 2026-10-17). */
function rights(canEdit: boolean, canDelete: boolean): EditRights {
  return canEdit ? (canDelete ? FULL_RIGHTS : EDIT_ONLY) : canDelete ? DELETE_ONLY : NO_EDIT_RIGHTS;
}

/** `permissions`: the reader's set where the post lives — the channel's snapshot for a channel, the
 *  system roles' for a DM or group message (`postPermissionsKey`). Empty = the own-post rule alone. */
export function editRights(post: Pick<PostRecord, 'userId' | 'state' | 'type'>, currentUserId: string, permissions: ReadonlySet<string>): EditRights {
  if (post.state !== PostState.Synced) return NO_EDIT_RIGHTS;
  if (post.userId === currentUserId) return FULL_RIGHTS;
  if (post.type) return NO_EDIT_RIGHTS;
  return rights(permissions.has(EDIT_OTHERS_POSTS), permissions.has(DELETE_OTHERS_POSTS));
}

/** Same names as the native stores (Swift `SyncEngine.systemPermissionsKey`). */
export const SYSTEM_PERMISSIONS_KEY = 'system_permissions';

/** Where the permissions governing a post in `channelType` live (Swift `postPermissionsKey`). */
export function postPermissionsKey(channelId: string, channelType: string): string {
  return channelType === 'O' || channelType === 'P' ? `channel_permissions_${channelId}` : SYSTEM_PERMISSIONS_KEY;
}
